// material-ui
import Box from "@mui/material/Box";
import { styled, Theme, useTheme } from "@mui/material/styles";
import MuiTooltip, { tooltipClasses, TooltipProps } from "@mui/material/Tooltip";

// type
import { ColorProps } from "@/types/extended";
// project import
import getColors from "@/utils/getColors";

// ==============================|| TOOLTIP - VARIANT ||============================== //

interface TooltipStyleProps {
  color?: ColorProps | string;
  labelColor?: ColorProps | string;
  theme: Theme;
}

function getVariantStyle({ color, theme, labelColor }: TooltipStyleProps) {
  const colors = getColors(theme, color as ColorProps);
  const { main, contrastText } = colors;
  const colorValue = color || "";

  // Shared styles for the tooltip
  const baseTooltipStyle = {
    background: colorValue,
    color: labelColor || contrastText,
    boxShadow: theme.shadows[1],
    borderRadius: "8px",
    maxWidth: "310px",
    padding: "6px 8px",
  };

  return {
    [`& .${tooltipClasses.tooltip}`]: {
      ...baseTooltipStyle,
      background: ["primary", "secondary", "info", "success", "warning", "error"].includes(
        colorValue
      )
        ? main
        : colorValue,
    },
    [`& .${tooltipClasses.arrow}`]: {
      color: ["primary", "secondary", "info", "success", "warning", "error"].includes(colorValue)
        ? main
        : colorValue,
    },
  };
}

// ==============================|| STYLED - TOOLTIP COLOR ||============================== //

interface StyleProps {
  theme: Theme;
  arrow: TooltipProps["arrow"];
  labelColor?: ColorProps | string;
  color?: ColorProps | string;
}

const TooltipStyle = styled(
  ({ className, ...props }: TooltipProps) => (
    <MuiTooltip {...props} classes={{ popper: className }} />
  ),
  {
    shouldForwardProp: (prop) => prop !== "color" && prop !== "labelColor",
  }
)(({ theme, color, labelColor }: StyleProps) => ({
  ...(color && getVariantStyle({ color, theme, labelColor })),
  [`& .${tooltipClasses.tooltip}`]: {
    textAlign: "center",
    borderRadius: "8px",
    maxWidth: "260px",
    padding: "12px 16px",
    fontSize: "12px",
  },
}));

// ==============================|| EXTENDED - TOOLTIP ||============================== //

interface Props extends TooltipProps {
  arrow?: boolean;
  color?: ColorProps | string;
  labelColor?: ColorProps | string;
  children: TooltipProps["children"];
}

export default function CustomTooltip({ children, arrow = true, labelColor = "", ...rest }: Props) {
  const theme = useTheme();
  return (
    <Box display="flex">
      <TooltipStyle arrow={arrow} {...rest} theme={theme} labelColor={labelColor}>
        {children}
      </TooltipStyle>
    </Box>
  );
}
